import { format, parseISO } from 'date-fns'
import { IconType } from 'react-icons'
import { FiClipboard, FiDownload, FiMapPin, FiPlusCircle, FiUsers } from 'react-icons/fi'
import { ReleaseAnnouncementFeatureTheme, ReleaseAnnouncementPayload } from './types'

const ICON_REGISTRY: Record<string, IconType> = {
  users: FiUsers,
  clipboard: FiClipboard,
  'map-pin': FiMapPin,
  download: FiDownload,
  'plus-circle': FiPlusCircle,
}

const DEFAULT_THEME = {
  iconBg: 'gray.50',
  iconColor: 'gray.600',
}

export const WHATS_NEW_MODAL_FLAG_KEY = 'whats-new-modal'

export const getAnnouncementDismissKey = (id: string) => `announcement-${id}`

const DISMISSED_VALUE = 'true'

const dismissedAnnouncementIds = new Set<string>()

const readDismissFromStorage = (storage: Storage, key: string): boolean => {
  try {
    return storage.getItem(key) === DISMISSED_VALUE
  } catch {
    return false
  }
}

const writeDismissToStorage = (storage: Storage, key: string): void => {
  try {
    storage.setItem(key, DISMISSED_VALUE)
  } catch {
    // storage unavailable (private browsing, quota, Safari restrictions, etc.)
  }
}

export const isAnnouncementPermanentlyDismissed = (id: string): boolean => {
  if (dismissedAnnouncementIds.has(id)) return true

  const key = getAnnouncementDismissKey(id)
  return (
    readDismissFromStorage(window.localStorage, key) ||
    readDismissFromStorage(window.sessionStorage, key)
  )
}

export const persistAnnouncementPermanentDismiss = (id: string): void => {
  dismissedAnnouncementIds.add(id)

  const key = getAnnouncementDismissKey(id)
  writeDismissToStorage(window.localStorage, key)
  writeDismissToStorage(window.sessionStorage, key)
}

export const resolveFeatureIcon = (iconKey: string): IconType =>
  ICON_REGISTRY[iconKey] ?? FiPlusCircle

export const resolveFeatureTheme = (theme?: ReleaseAnnouncementFeatureTheme) => ({
  iconBg: theme?.iconBg ?? DEFAULT_THEME.iconBg,
  iconColor: theme?.iconColor ?? DEFAULT_THEME.iconColor,
})

export const formatPublishedAt = (publishedAt: string): string => {
  try {
    return format(parseISO(publishedAt), 'd MMM yyyy')
  } catch {
    return publishedAt
  }
}

export const isReleaseAnnouncementPayload = (
  value: unknown
): value is ReleaseAnnouncementPayload => {
  if (!value || typeof value !== 'object') return false

  const payload = value as ReleaseAnnouncementPayload

  return (
    typeof payload.id === 'string' &&
    typeof payload.publishedAt === 'string' &&
    !!payload.header &&
    typeof payload.header.badgeLabel === 'string' &&
    typeof payload.header.title === 'string' &&
    Array.isArray(payload.features) &&
    payload.features.length > 0 &&
    !!payload.footer &&
    typeof payload.footer.dismissCheckboxLabel === 'string' &&
    typeof payload.footer.primaryButtonLabel === 'string'
  )
}
